import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, HeartHandshake, Video, CheckCircle2 } from "lucide-react";
import { Hero } from "../components/Hero";
import { StatsCounter } from "../components/StatsCounter";
import { ServiceCard } from "../components/ServiceCard";
import { NEUROLOGY_SERVICES, PSYCHOLOGY_SERVICES, IMAGES, CLINIC } from "../lib/clinic";

const Home = () => {
  return (
    <div data-testid="home-page">
      <Hero />

      {/* Editorial intro line */}
      <section className="py-20 md:py-28 bg-paper" data-testid="intro-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-5">A Note From Dr. Avril</div>
              <p className="heading-serif text-3xl md:text-4xl lg:text-[2.5rem] text-brand-navyDark leading-[1.3] font-medium">
                "Every patient walks in with a story. My work is to listen first —
                <span className="italic text-brand-tealDark"> then explain, then treat, </span>
                with the same care I would want for my own family."
              </p>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <div className="w-12 h-px bg-brand-goldDark mb-5" />
              <p className="text-[14px] text-brand-body leading-relaxed">
                With over fifteen years across government hospitals and private consultation,
                Dr. Avril Dhar brings together the precision of modern neuroscience and the
                empathy of psychology — for whole-person care in Chandigarh and online.
              </p>
            </div>
          </div>
        </div>
      </section>

      <StatsCounter />

      {/* Specialties — asymmetric editorial layout */}
      <section className="section-y bg-paper" data-testid="specialties-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-8 mb-20">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5">Two Specialties</div>
              <h2 className="heading-serif text-4xl md:text-5xl lg:text-6xl text-brand-navyDark leading-[1.04] font-medium">
                Caring for both the <span className="italic">brain</span> and the <span className="italic">mind</span>.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 lg:pt-4">
              <p className="text-[16px] text-brand-body leading-[1.8]">
                Modern neurology and clinical psychology — practiced together, by one
                doctor. So your physical symptoms and emotional well-being are understood
                as part of the same story.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-stone-200">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-paper p-10 md:p-14"
            >
              <Brain className="w-7 h-7 text-brand-tealDark mb-7" strokeWidth={1.3} />
              <h3 className="heading-serif text-3xl md:text-4xl font-medium text-brand-navyDark mb-4">
                Neurology
              </h3>
              <p className="text-brand-body leading-[1.8] mb-7">
                Headaches, stroke, vertigo, Parkinson's, memory issues, seizures and more —
                managed with modern protocols and a steady hand.
              </p>
              <ul className="space-y-3 mb-9">
                {[
                  "Migraine & Chronic Headaches",
                  "Stroke Prevention & Recovery",
                  "Memory & Cognitive Care",
                  "Parkinson's & Movement Disorders",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-[14px] text-brand-navyDark">
                    <CheckCircle2 className="w-4 h-4 text-brand-tealDark mt-0.5 shrink-0" strokeWidth={1.5} />
                    {x}
                  </li>
                ))}
              </ul>
              <Link to="/neurology" className="btn-secondary" data-testid="explore-neurology">
                Explore Neurology <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-paper-soft p-10 md:p-14"
            >
              <HeartHandshake className="w-7 h-7 text-brand-goldDark mb-7" strokeWidth={1.3} />
              <h3 className="heading-serif text-3xl md:text-4xl font-medium text-brand-navyDark mb-4">
                Psychology
              </h3>
              <p className="text-brand-body leading-[1.8] mb-7">
                Anxiety, depression, sleep, panic, relationships and emotional wellness —
                in a safe, non-judgmental space.
              </p>
              <ul className="space-y-3 mb-9">
                {[
                  "Anxiety & Panic Disorders",
                  "Depression & Mood Care",
                  "Sleep & Stress Management",
                  "Confidence & Emotional Health",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-[14px] text-brand-navyDark">
                    <CheckCircle2 className="w-4 h-4 text-brand-goldDark mt-0.5 shrink-0" strokeWidth={1.5} />
                    {x}
                  </li>
                ))}
              </ul>
              <Link to="/psychology" className="btn-secondary" data-testid="explore-psychology">
                Explore Psychology <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About preview — asymmetric */}
      <section className="section-y bg-paper-soft" data-testid="about-preview">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -left-4 -top-4 w-24 h-24 border border-brand-goldDark/40 -z-10" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.doctor_desk}
                alt="Dr. Avril Dhar consulting"
                className="w-full h-full object-cover grayscale-[10%]"
              />
            </div>
            <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-brand-goldDark heading-serif italic">
              Personalized patient-focused consultation
            </p>
          </motion.div>

          <div className="lg:col-span-6 lg:col-start-7">
            <div className="eyebrow mb-5">About Dr. Avril Dhar</div>
            <h2 className="heading-serif text-4xl md:text-5xl text-brand-navyDark leading-[1.08] font-medium mb-7">
              Fifteen years of compassionate, expert care.
            </h2>
            <p className="text-brand-body leading-[1.85] mb-5 text-[15px]">
              With training in both neurology and psychology, Dr. Avril combines the
              precision of modern neuroscience with the empathy of mental health
              practice. Her background spans government hospital experience and private
              consultation — bringing depth, warmth and clarity to every visit.
            </p>
            <ul className="grid grid-cols-2 gap-x-8 gap-y-3 my-8 max-w-md">
              {["MBBS", "MS", "MCh", "Psychology"].map((q) => (
                <li key={q} className="flex items-center gap-3 py-2 border-b border-stone-200/80">
                  <span className="text-brand-goldDark text-xs">—</span>
                  <span className="text-[14px] font-medium text-brand-navyDark tracking-wide">
                    {q}
                  </span>
                </li>
              ))}
            </ul>
            <Link to="/about" className="btn-secondary" data-testid="learn-more-about">
              Read full profile <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Conditions preview */}
      <section className="section-y bg-paper" data-testid="top-services">
        <div className="container-x">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-5">Conditions We Treat</div>
            <h2 className="heading-serif text-4xl md:text-5xl text-brand-navyDark leading-[1.06] font-medium">
              Care across neurology and mental wellness.
            </h2>
            <p className="mt-5 text-brand-body leading-[1.8]">
              A snapshot of the most common conditions. Every treatment plan is
              personal — built around your story, your symptoms and your goals.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200">
            {[...NEUROLOGY_SERVICES.slice(0, 3), ...PSYCHOLOGY_SERVICES.slice(0, 3)].map((s, i) => (
              <ServiceCard
                key={s.title}
                {...s}
                index={i}
                accent={i < 3 ? "sage" : "gold"}
              />
            ))}
          </div>
          <div className="mt-14 flex flex-wrap gap-3">
            <Link to="/neurology" className="btn-secondary" data-testid="view-neurology">
              All Neurology Services
            </Link>
            <Link to="/psychology" className="btn-primary" data-testid="view-psychology">
              All Psychology Services
            </Link>
          </div>
        </div>
      </section>

      {/* Online consultation banner — quieter, editorial */}
      <section className="section-y bg-paper-soft" data-testid="online-cta">
        <div className="container-x">
          <div className="relative bg-brand-navyDark px-8 py-16 md:px-20 md:py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3 mb-7">
                  <span className="w-10 h-px bg-brand-gold" />
                  <span className="text-[11px] tracking-[0.28em] uppercase font-medium text-brand-gold">
                    Consult From Home
                  </span>
                </div>
                <h3 className="heading-serif text-4xl md:text-5xl text-white font-medium leading-[1.08] mb-6">
                  Premium online consultation — anywhere in India.
                </h3>
                <p className="text-stone-300 leading-[1.8] mb-9 max-w-xl">
                  Book a confidential video consultation. Get a clear diagnosis, prescription
                  and a personalized treatment plan — all without leaving your home.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/online-consultation"
                    className="inline-flex items-center gap-2 bg-[#FAF9F7] text-brand-navyDark px-7 py-3.5 text-[13px] tracking-[0.06em] uppercase font-medium hover:bg-brand-gold transition-all"
                    data-testid="online-cta-btn"
                  >
                    <Video className="w-3.5 h-3.5" /> Start Consultation
                  </Link>
                  <a
                    href={CLINIC.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 text-[13px] tracking-[0.06em] uppercase font-medium hover:bg-white hover:text-brand-navyDark transition-all"
                    data-testid="online-cta-whatsapp"
                  >
                    WhatsApp Now
                  </a>
                </div>
              </div>
              <div className="lg:col-span-4 hidden lg:flex flex-col items-end">
                <span className="heading-serif text-[140px] leading-none text-brand-gold/30 italic">
                  online.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-y bg-paper" data-testid="final-cta">
        <div className="container-x max-w-3xl">
          <div className="eyebrow mb-5">Ready when you are</div>
          <h2 className="heading-serif text-4xl sm:text-5xl md:text-[3.75rem] text-brand-navyDark leading-[1.05] font-medium mb-7">
            Take the next step toward<br />
            <span className="italic text-brand-tealDark">better health, calmer mind.</span>
          </h2>
          <p className="text-brand-body mb-10 text-[16px] leading-[1.8] max-w-xl">
            Book your consultation today — in clinic or online. We respond within hours.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact#book" className="btn-primary" data-testid="final-book">
              Book Appointment
            </Link>
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="btn-secondary"
              data-testid="final-call"
            >
              Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
